/**
 * Pure helpers for analytics properties. No Mixpanel, no React, so they are
 * cheap to unit test (helpers.test.js).
 */
import { LINK_SOURCE, STORE } from './events';

/**
 * utm_source values the links carry today, mapped to the plan's source enum.
 * Both the legacy spellings and the normalized ones map, so a link minted by
 * a newer app version keeps working.
 */
const SOURCE_BY_UTM = Object.freeze({
  osshare: LINK_SOURCE.SHARE_SHEET,
  share_sheet: LINK_SOURCE.SHARE_SHEET,
  smsblast: LINK_SOURCE.SMS_BLAST,
  sms_blast: LINK_SOURCE.SMS_BLAST,
  qrshare: LINK_SOURCE.QR,
  qr_code: LINK_SOURCE.QR,
  qr: LINK_SOURCE.QR,
  copylink: LINK_SOURCE.COPY_LINK,
  copy_link: LINK_SOURCE.COPY_LINK,
  receipttext: LINK_SOURCE.RECEIPT_TEXT,
  receipt_text: LINK_SOURCE.RECEIPT_TEXT,
  ghost_sms: LINK_SOURCE.GROUP_INVITE_TEXT,
  group_invite_text: LINK_SOURCE.GROUP_INVITE_TEXT,
  nudge: LINK_SOURCE.NUDGE,
});

/**
 * The raw utm_source from a query string, or undefined when absent.
 * @param {string | null | undefined} search window.location.search or location.search
 * @returns {string | undefined}
 */
export function readUtmSource(search) {
  return new URLSearchParams(search || '').get('utm_source') || undefined;
}

/**
 * Normalize a utm_source per the tracking plan's source rule:
 * osshare -> share_sheet, smsblast -> sms_blast, qrshare -> qr,
 * copylink -> copy_link, receipttext -> receipt_text, ghost_sms ->
 * group_invite_text, nudge -> nudge, missing -> `fallback` (direct).
 *
 * Corrupted values like "smsblast?utm_source=osshare" (a link that already had
 * a query string and got a second one appended) resolve to their first token.
 * An unknown but present value is passed through snake_cased rather than
 * folded into direct, so a new link source shows up under its own name.
 *
 * @param {string | null | undefined} utmSource
 * @param {string} [fallback] source when utm_source is missing
 * @returns {import('./events').LinkSource | string}
 */
export function normalizeLinkSource(utmSource, fallback = LINK_SOURCE.DIRECT) {
  if (typeof utmSource !== 'string') return fallback;
  const token = utmSource.split('?')[0].trim().toLowerCase();
  if (!token) return fallback;
  if (SOURCE_BY_UTM[token]) return SOURCE_BY_UTM[token];
  const sanitized = token.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  return sanitized || fallback;
}

/**
 * Which app store this browser belongs to, from the user agent. undefined on
 * desktop and unknown devices so app_download_clicked never guesses a store.
 * @param {string} [userAgent] defaults to navigator.userAgent
 * @returns {import('./events').Store | undefined}
 */
export function detectStore(userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '') {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return STORE.IOS;
  if (/Android/i.test(userAgent)) return STORE.ANDROID;
  return undefined;
}

/**
 * A trimmed string for an optional property, or undefined when the value is
 * missing or blank, so track() and setPeople() drop it instead of sending
 * null or "". Used for the names of places (restaurant_name) and for the
 * payout handles that go on the profile.
 * @param {unknown} value
 * @returns {string | undefined}
 */
export function optionalString(value) {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}
